'use client'

import { useEffect } from 'react'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { usePathname } from 'next/navigation'
import { useUser } from '@clerk/nextjs'
import posthog from '@/lib/posthog'
import { useCustomer } from '@/lib/hooks/use-customer'
import { trackSignup } from '@/lib/analytics'

const POSTHOG_SIGNUP_TRACKED_KEY = 'ph_signup_tracked'

/**
 * PostHog Analytics Provider
 *
 * This component wraps the app with PostHog's React provider and handles:
 * - User identification when logged in
 * - Automatic page view tracking
 * - User logout (reset)
 * - Syncing user properties from Clerk and billing
 *
 * Best Practices Implemented:
 * - Uses user ID (clerk_id) as distinct_id, not email
 * - Only identifies authenticated users (person_profiles: 'identified_only')
 * - Calls reset() on logout to clear user data
 * - Tracks comprehensive user properties for better analytics
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { user: clerkUser, isSignedIn } = useUser()
  const { customer } = useCustomer()

  // Track page views on route changes
  useEffect(() => {
    if (pathname && typeof window !== 'undefined') {
      posthog.capture('$pageview', {
        $current_url: pathname,
        url: pathname,
      })
    }
  }, [pathname])

  // Handle user identification and logout
  useEffect(() => {
    if (isSignedIn && clerkUser) {
      // User is authenticated - identify them in PostHog
      // Using Clerk user ID as distinct_id (best practice - stable, unique, not PII)
      const distinctId = clerkUser.id

      posthog.identify(distinctId, {
        // Identity
        clerk_id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress,
        name: clerkUser.fullName,
        first_name: clerkUser.firstName,
        last_name: clerkUser.lastName,
        image_url: clerkUser.imageUrl,

        // Billing info from useCustomer
        plan: customer?.product_id,
        has_subscription: customer?.has_active_subscription,
        videos_balance: customer?.features?.['credits']?.balance,
        videos_limit: customer?.features?.['credits']?.included_usage,

        // Account info
        created_at: clerkUser.createdAt,
        updated_at: clerkUser.updatedAt,
      })

      // Fire user_signed_up event for accounts created within the last 5 minutes
      // Uses Clerk createdAt instead of localStorage to avoid inflating metrics
      // on new devices, cleared storage, or existing users after deployment
      try {
        const tracked = localStorage.getItem(POSTHOG_SIGNUP_TRACKED_KEY)
        if (!tracked && clerkUser.createdAt) {
          const accountAgeMs = Date.now() - new Date(clerkUser.createdAt).getTime()
          const FIVE_MINUTES_MS = 5 * 60 * 1000
          if (accountAgeMs < FIVE_MINUTES_MS) {
            trackSignup()
          }
          // Mark as checked regardless — only fire once per browser
          localStorage.setItem(POSTHOG_SIGNUP_TRACKED_KEY, '1')
        }
      } catch {
        // localStorage may be unavailable
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[PostHog] User identified:', distinctId)
      }
    } else if (!isSignedIn) {
      // User logged out - reset PostHog to clear user data
      posthog.reset()

      if (process.env.NODE_ENV === 'development') {
        console.log('[PostHog] User logged out - reset called')
      }
    }
  }, [isSignedIn, clerkUser, customer])

  return <PHProvider client={posthog}>{children}</PHProvider>
}
